import { createSlice } from '@reduxjs/toolkit';
import _isString from 'lodash/isString';
import _pick from 'lodash/pick';
import axios from 'cccisd-axios';

const Boilerplate = window.cccisd.boilerplate;

// Initial state
export const initialState = {
    currentDate: new Date().getTime(),
    clubAccessId: '',
    clubInfo: {},
    memberId: '0',
    memberPawnId: 0,
    memberPawnHash: '',
    birthday: '',
    language: 'en',
    page: 'pickMemberId',
    pageProps: {},
    isPlainSurvey: false,
};

const bgClubSlice = createSlice({
    name: 'bgca/bgClub',
    initialState: () => initialState,
    reducers: {
        setClubInfo: (state, action) => {
            state.clubAccessId = action.payload.clubAccessId;
            state.clubInfo = _pick(action.payload, [
                'groupId',
                'clubId',
                'clubAccessId',
                'label',
                'artsModuleSelected',
                'bullyingModuleSelected',
                'ilModuleSelected',
                'riskBehaviorModuleSelected',
                'stemModuleSelected',
                'legacyModuleSelected',
                'essentialSkillsModuleSelected',
                'charLeadershipModuleSelected',
                'downloadSpeedCount',
                'downloadSpeedHistory',
                'avgDownloadSpeed',
            ]);
        },
        setMemberId: (state, action) => {
            state.memberId = action.payload.replace(/^0+/, '').toLowerCase();
        },
        setMemberPawnId: (state, action) => {
            state.memberPawnId = action.payload;
        },
        setMemberPawnHash: (state, action) => {
            state.memberPawnHash = action.payload;
        },
        setBirthday: (state, action) => {
            state.birthday = action.payload;
        },
        setLanguage: (state, action) => {
            state.language = action.payload;
        },
        setIsPlainSurvey: (state, action) => {
            const isPlainSurvey = Boolean(action.payload);

            if (state.isPlainSurvey !== isPlainSurvey) {
                state.isPlainSurvey = isPlainSurvey;
            }
        },
        goToPage: (state, action) => {
            const { name, props = {} } = _isString(action.payload) ? { name: action.payload } : action.payload;

            state.page = name;
            state.pageProps = props;
        },
        resetMemberInfo: (state, action) => {
            Object.assign(
                state,
                _pick(initialState, [
                    'memberId',
                    'memberPawnId',
                    'memberPawnHash',
                    'birthday',
                    'language',
                    'page',
                    'pageProps',
                ])
            );
        },
    },
});

export default bgClubSlice.reducer;
export const persistedKeys = ['clubAccessId', 'isPlainSurvey'];
export const {
    setClubInfo,
    setMemberId,
    setMemberPawnId,
    setMemberPawnHash,
    setBirthday,
    setLanguage,
    setIsPlainSurvey,
    goToPage,
    resetMemberInfo,
} = bgClubSlice.actions;

export const getMembers = () => {
    return async (dispatch, getState) => {
        const { clubInfo, memberId } = getState().app.bgClub;
        const routeName = 'get.club.student';

        const result = await axios.get(
            Boilerplate.route(routeName, {
                groupId: clubInfo.groupId,
                memberId,
            })
        );

        return result.data.students.map(item => ({
            pawnId: item.pawnId,
            pawnHash: item.pawnHash,
            birthday: item.birthday,
            language: item.language,
            isFlowFinished: !!item.isFlowFinished,
        }));
    };
};

export const saveScreenedUser = () => {
    return async (dispatch, getState) => {
        const state = getState().app.bgClub;
        const params = {
            ..._pick(state, ['memberId', 'birthday']),
            role: 'student',
            setAsBehalf: true,
            screened: true,
            group: state.clubInfo.groupId,
            selfRoster: true,
        };

        let result = await axios.post(Boilerplate.route('api.nexus.pawn.store'), params);

        let apParams = {
            pawnId: result.data.data.id,
            deploymentId: 94, // youth-main-2027
            flowlist: 'default',
            language: 'en',
        };

        await axios.post(Boilerplate.route('api.assignmentProgress.update'), apParams);
    };
};

export const useNewPawnId = (role = 'student') => {
    return async (dispatch, getState) => {
        const state = getState().app.bgClub;
        const params = {
            ..._pick(state, ['memberId', 'birthday', 'language']),
            role,
            setAsBehalf: true,
            screened: false,
            group: state.clubInfo.groupId,
            selfRoster: true,
        };

        const result = await axios.post(Boilerplate.route('api.nexus.pawn.store'), params);

        const pawnId = result.data.data.id;
        const pawnHash = result.data.data.random_hash;
        dispatch(setMemberPawnId(pawnId));
        dispatch(setMemberPawnHash(pawnHash));
    };
};

export const updatePawnId = () => {
    return async (dispatch, getState) => {
        const { memberPawnId, memberPawnHash, language } = getState().app.bgClub;

        await axios.put(Boilerplate.route('api.nexus.pawn.update', { pawn: memberPawnId }), {
            language,
            pawnHash: memberPawnHash,
            screened: false,
        });
    };
};

export const finishSurvey = () => {
    return async (dispatch, getState) => {
        const state = getState().app.bgClub;

        if (!state.memberPawnId) {
            return;
        }

        await axios.put(Boilerplate.route('api.nexus.pawn.update', { pawn: state.memberPawnId }), {
            pawnId: state.memberPawnId,
            pawnHash: state.memberPawnHash,
            screened: false,
            isFlowFinished: true,
        });
    };
};

export const loadClubInfo = (organizerUrl = '/organizer') => {
    return async (dispatch, getState) => {
        const { clubAccessId } = getState().app.bgClub;

        // Redirect to organizer if clubAccessId is not a 6-digit number
        if (!/^\d{6}$/.test(clubAccessId)) {
            window.location.replace(Boilerplate.url(organizerUrl));
        }

        const result = await axios.get(Boilerplate.route('get.club.info', { accessCodeId: clubAccessId }));

        if (
            result.data.status !== 'success' ||
            result.data.club.participatingThisYear !== 1 ||
            result.data.club.surveyMethod !== 'online'
        ) {
            // Redirect to organizer if we cannon find this club
            window.location.replace(Boilerplate.url(organizerUrl));
        } else {
            dispatch(setClubInfo(result.data.club));
        }
    };
};
